import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionSummary,
  Autocomplete,
  AutocompleteChangeReason,
  Button,
  Card,
  Checkbox,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { useQuery } from "@tanstack/react-query";
import { ptBR } from "date-fns/locale";
import { Dispatch,SetStateAction,useContext,useEffect,useState } from "react";
import { useTranslation } from "react-i18next";
import { IoFilter } from "react-icons/io5";
import { toast } from "react-toastify";
import { UserContext } from "../../context/UserContext";
import AttToken from "../../helpers/attToken";
import { trackEventMatomo } from "../../helpers/matomo";
import Checklists from "../../service/Checklists";
import Clients from "../../service/Clients";
import Users from "../../service/Users";
import { FilterAprovationsV2Type,hierarchyResponse,nomeIdSons } from "../../types/aprovations/types";
import { ArrayAutocomplete,AutoCompleteEvent } from "../../types/helpers/types";
import AutocompleteWithApi from "../Autocomplete/AutocompleteWithApi";
type props = {
  filter: FilterAprovationsV2Type;
  setFilter: Dispatch<SetStateAction<FilterAprovationsV2Type>>;
  refetch: Function;
  dates: { dataInicio: number; dataFim: number };
  setDates: Function;
  commingFromHistory: boolean;
};

const clientService = new Clients();
const formSevice = new Checklists();
const userService = new Users();

type keyOptions = "clientes" | "locais" | "conjuntos" | "unidades";

export default function FiltersAprovationComponent({ filter, setFilter, refetch, dates, setDates, commingFromHistory }: props) {
  const { t } = useTranslation("translation");
  const { userData } = useContext(UserContext);

  const [values, setValues] = useState<{ [key: string]: { label: string; value: string }[] }>({
    clientes: [],
    locais: [],
    conjuntos: [],
    unidades: [],
  });
  const [forms, setForms] = useState<ArrayAutocomplete>([]);
  const [users, setUsers] = useState<ArrayAutocomplete>([]);
  const [openForms, setOpenForms] = useState(false);
  const [openUsers, setOpenUsers] = useState(false);
  const [value, setValue] = useState("all");

  const {
    data: formsData,
    isLoading: isLoadingForms,
    isFetching: isFetchingForms,
    isSuccess: successForms,
  } = useQuery({
    queryKey: ["GET_FORMS", openForms],
    queryFn: async () => {
      try {
        toast.info("Buscando modelos de formulário", { toastId: "buscandoForms" });
        const token = await AttToken();
        if (!token) throw new Error("Token not found");
        if (token) {
          const response: { nome: string; id: string }[] = await formSevice.getSimpleForm(token);
          if (!response) throw new Error("Forms not found");
          if (response) {
            return response;
          }
        }
      } catch (err) {
        throw new Error("Error fetching forms");
      }

    },
    enabled: openForms,
    retry: 2,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  const {
    data: usersData,
    isLoading: isLoadingUsers,
    isFetching: fetchingUsers,
    isSuccess: successUsers,
  } = useQuery({
    queryKey: ["GET_USERS", openUsers],
    queryFn: async () => {
      try {
        toast.info("Buscando usuários!", { toastId: "buscandoUsers" });
        const token = await AttToken();
        if (!token) throw new Error("Token not found");
        if (token) {
          const response = await userService.getAllUsers(token);
          if (!response) throw new Error("Users not found");
          if (response) {
            return response.users;
          }
        }
      } catch (err) {
        throw new Error("Error fetching users");
      }
    },
    enabled: openUsers,
    retry: 2,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  const {
    data: hierarchy,
    isLoading,
    isFetching,
    isSuccess,
    isError,
  } = useQuery({
    queryKey: ["GET_FULL_HIEARCHY"],
    queryFn: async () => {
      try {
        toast.info(t("Aprovations.fetchingFilters"), { toastId: "buscando" });
        const token = await AttToken();
        if (!token) throw new Error("Token not found");
        if (token) {
          const data: hierarchyResponse = await clientService.getAllHierarchy(token);
          if (!data) throw new Error("Hierarchy not found");
          return data;
        }
      } catch (err) {
        throw new Error(`Erro ao buscar filtros: ${err}`);
      }
    },
    retry: 3,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchInterval: 5 * 60 * 1000,
  });

  const loading = isLoading || isFetching || isLoadingForms || isLoadingUsers || isFetchingForms || fetchingUsers;

  useEffect(() => {
    if (usersData && successUsers) {
      toast.success("Usuários encontrados", { toastId: "sucessoUsers" });
    }
    if (successForms && formsData) {
      toast.success("Modelos encontrados", { toastId: "sucessoForms" });
    }
    if (isSuccess && hierarchy) {
      toast.success(t("Aprovations.filtersFound"), { toastId: "sucesso" });
    }
    if (isError) {
      toast.error(t("Aprovations.errorFetching"), { toastId: "erro" });
    }
  }, [loading, isSuccess, isError, hierarchy, t, successForms, formsData, successUsers, usersData]);

  const verifySons = (
    values: { label: string; value: string }[],
    keyFather: keyOptions,
    idSon: string,
    keySons: "locais" | "conjuntos" | "unidades",
  ) => {
    return values?.some((val) => {
      if (hierarchy) {
        const current = hierarchy?.[keyFather]?.find((el) => el.id === val.value);
        const sons = current ? ((current as nomeIdSons)[keySons] ?? []) : [];
        if (sons && sons.length) {
          return sons?.includes(idSon);
        }
      }
      return false;
    });
  };

  const removeSons = (
    fatherIds: string[],
    keyFather: keyOptions,
    keySons: "locais" | "conjuntos" | "unidades",
    keyFilter: "cliente" | "local" | "conjunto" | "unidade",
  ) => {
    if (hierarchy) {
      const result: string[] = [];
      fatherIds?.forEach((id) => {
        const current = hierarchy?.[keyFather]?.find((el) => el.id === id);
        const sons = current ? ((current as nomeIdSons)[keySons] ?? []) : [];
        if (sons && sons.length) {
          sons?.forEach((sonId: string) => {
            setValues((prev) => {
              if (prev[keySons] && prev[keySons]?.some((el) => el.value === sonId)) {
                return { ...prev, [keySons]: prev[keySons]?.filter((el) => el.value !== sonId) };
              } else return prev;
            });
            setFilter((prev) => {
              const filters = prev[keyFilter];
              if (filters && filters.some((ind) => ind === sonId)) {
                return { ...prev, [keyFilter]: filters?.filter((ind) => ind !== sonId) };
              } else return prev;
            });
            result.push(sonId);
          });
        }
      });
      return result;
    } else return [];
  };

  useEffect(() => {
    let formArray: string[] = [];
    let userArray: string[] = [];
    users?.map((ele) => userArray.push(ele.value));
    forms?.map((ele) => formArray.push(ele.value));
    setFilter((prev) => ({ ...prev, form: formArray, usuario: userArray }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forms, users]);

  return (
    <Card sx={{ padding: 2, marginBottom: "20px" }}>
      <Stack direction="column" spacing={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" justifyContent="flex-start" alignItems="center" spacing={1}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
              <DesktopDatePicker
                disabled={loading}
                format="dd/MM/yyyy"
                label={t("notifications.unread.startAt")}
                value={new Date(dates.dataInicio ? Number(dates.dataInicio) : Date.now())}
                maxDate={new Date(dates.dataFim)}
                onChange={(newValue) => {
                  if (!newValue) return;
                  const date = new Date(newValue.getTime());
                  date.setHours(0, 0, 0, 0);
                  trackEventMatomo("Aprovações", "click", "datePicker", "altera data");
                  setDates({ ...dates, dataInicio: date });
                  setFilter((prev) => ({ ...prev, inicio: { ...prev.inicio, de: date.getTime() } }));
                }}
                slotProps={{
                  textField: { size: "small" },
                  field: {
                    readOnly: true,
                  },
                }}
              />
              <DesktopDatePicker
                disabled={loading}
                format="dd/MM/yyyy"
                label={t("Reports.columns.end")}
                value={new Date(dates.dataFim ? Number(dates.dataFim) : Date.now())}
                onChange={(newValue) => {
                  if (!newValue) return;
                  const date = new Date(newValue.getTime());
                  date.setHours(23, 59, 59, 59);
                  setDates({ ...dates, dataFim: date });
                  setFilter((prev) => ({ ...prev, inicio: { ...prev.inicio, ate: date.getTime() } }));
                }}
                slotProps={{
                  textField: { size: "small" },
                  field: {
                    readOnly: true,
                  },
                }}
              />
            </LocalizationProvider>
          </Stack>
          <Button
            disabled={loading}
            endIcon={<IoFilter />}
            onClick={() => {
              refetch();
              trackEventMatomo("Aprovações", "click", "button", "filtra");
            }}
            variant="contained"
            color="success">
            {t("filter")}
          </Button>
        </Stack>
        <Accordion style={{ marginBottom: "10px" }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} onClick={() => trackEventMatomo("Aprovações", "click", "accordeon", "abre/fecha")}>
            <Typography color="textDisabled" fontWeight={600} variant="h5" width="100%" textAlign="center">
              {t("Aprovations.filters")}
            </Typography>
          </AccordionSummary>
          <Stack padding={2} width="100%" direction="column" justifyContent="center" alignItems="center" spacing={1.5}>
            <Stack width="100%" spacing={1} direction="row" justifyContent="space-between" alignItems="center">
              <AutocompleteWithApi
                options={formsData?.map((form) => ({ value: form.id, label: form.nome })) ?? []}
                selectedValues={forms ?? []}
                onChange={(values) => setForms(values as { value: string; label: string }[])}
                loading={loading}
                setOpen={setOpenForms}
                textLabel="Selecione formulários"
                textoVazio="Sem formulários para listar"
                textoLoading="Buscando modelos de formulários..."
              />
              <AutocompleteWithApi
                options={usersData?.map((user: { id: string; nome: string }) => ({ value: user.id, label: user.nome })) ?? []}
                selectedValues={users ?? []}
                onChange={(values) => setUsers(values as { value: string; label: string }[])}
                loading={loading}
                setOpen={setOpenUsers}
                textLabel="Selecione usuários"
                textoVazio="Sem usuários para listar"
                textoLoading="Buscando usuários..."
              />
            </Stack>
            <Autocomplete
              disabled={loading || !hierarchy?.clientes?.length}
              fullWidth
              noOptionsText={`${t("Aprovations.noClientsFound")}`}
              multiple
              options={
                (hierarchy?.clientes as nomeIdSons[])
                  ?.sort((a, b) => {
                    return a.nome.toLowerCase() > b.nome.toLowerCase() ? 1 : -1;
                  })
                  ?.map((option) => ({
                    label: option.nome,
                    value: option.id,
                  })) || []
              }
              value={values["clientes"]}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderOption={(props, option, { selected }) => (
                <li {...props} key={option.value}>
                  <span>{option.label}</span>
                </li>
              )}
              onClick={() => trackEventMatomo("Aprovações", "click", "select", "seleciona clientes")}
              onChange={(_event: AutoCompleteEvent, newValue: { label: string; value: string }[] | null, reason: AutocompleteChangeReason, details) => {
                if (reason === "clear" && filter.cliente && filter.cliente.length) {
                  const removedLocais = removeSons(filter.cliente, "clientes", "locais", "local");
                  if (removedLocais && removedLocais.length) {
                    const removedConjuntos = removeSons(removedLocais, "locais", "conjuntos", "conjunto");
                    if (removedConjuntos && removedConjuntos.length) {
                      removeSons(removedConjuntos, "conjuntos", "unidades", "unidade");
                    }
                  }
                }
                if (reason === "removeOption") {
                  const removedCliente = details?.option.value as string;
                  const removedLocais = removeSons([removedCliente], "clientes", "locais", "local");
                  if (removedLocais && removedLocais.length) {
                    const removedConjuntos = removeSons(removedLocais, "locais", "conjuntos", "conjunto");
                    if (removedConjuntos && removedConjuntos.length) {
                      removeSons(removedConjuntos, "conjuntos", "unidades", "unidade");
                    }
                  }
                }
                if (reason === "clear") {
                  setFilter((prev) => {
                    delete prev.cliente;
                    return { ...prev };
                  });
                  setValues((prev) => {
                    if (prev) {
                      prev.clientes = [];
                      return prev ? { ...prev } : {};
                    } else return {};
                  });
                } else if (newValue && newValue.length >= 0) {
                  const values = newValue.map((val) => val.value);
                  setValues((prev) => (prev ? { ...prev, clientes: newValue } : { clientes: newValue }));
                  setFilter((prev) => ({ ...prev, cliente: values }));
                }
              }}
              renderInput={(params) => <TextField {...params} label={`${t("Aprovations.selectClients")}`} />}
            />
            <Autocomplete
              disabled={loading || !filter.cliente?.length}
              fullWidth
              noOptionsText={`${t("Aprovations.noMatchingLocations")}`}
              multiple
              options={
                hierarchy?.locais
                  ?.filter((lcl) => values.clientes.length && verifySons(values.clientes, "clientes", lcl.id, "locais"))
                  ?.sort((a, b) => {
                    return a.nome.toLowerCase() > b.nome.toLowerCase() ? 1 : -1;
                  })
                  ?.map((option) => ({
                    label: option.nome,
                    value: option.id,
                  })) || []
              }
              value={values["locais"]}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderOption={(props, option, { selected }) => (
                <li {...props} key={option.value}>
                  <span>{option.label}</span>
                </li>
              )}
              onClick={() => trackEventMatomo("Aprovações", "click", "select", "seleciona locais")}
              onChange={(_event: AutoCompleteEvent, newValue: { label: string; value: string }[] | null, reason: AutocompleteChangeReason, deatails) => {
                if (reason === "clear" && filter.local && filter.local.length) {
                  const removedConjuntos = removeSons(filter.local, "locais", "conjuntos", "conjunto");
                  if (removedConjuntos && removedConjuntos.length) {
                    removeSons(removedConjuntos, "conjuntos", "unidades", "unidade");
                  }
                }
                if (reason === "removeOption") {
                  const removedLocal = deatails?.option.value as string;
                  const removedConjuntos = removeSons([removedLocal], "locais", "conjuntos", "conjunto");
                  if (removedConjuntos && removedConjuntos.length) {
                    removeSons(removedConjuntos, "conjuntos", "unidades", "unidade");
                  }
                }
                if (reason === "clear") {
                  setFilter((prev) => {
                    delete prev.local;
                    return { ...prev };
                  });
                  setValues((prev) => {
                    if (prev) {
                      prev.locais = [];
                      return prev ? { ...prev } : {};
                    } else return {};
                  });
                } else if (newValue && newValue.length >= 0) {
                  const values = newValue.map((val) => val.value);
                  setValues((prev) => (prev ? { ...prev, locais: newValue } : { locais: newValue }));
                  setFilter((prev) => ({ ...prev, local: values }));
                }
              }}
              renderInput={(params) => <TextField {...params} label={`${t("Aprovations.selectLocations")}`} />}
            />
            <Autocomplete
              disabled={loading || !filter.local?.length}
              fullWidth
              noOptionsText={`${t("Aprovations.noMatchingSets")}`}
              multiple
              options={
                hierarchy?.conjuntos
                  ?.filter((cnj) => values.locais.length && verifySons(values.locais, "locais", cnj.id, "conjuntos"))
                  ?.sort((a, b) => {
                    return a.nome.toLowerCase() > b.nome.toLowerCase() ? 1 : -1;
                  })
                  ?.map((option) => ({
                    label: option.nome,
                    value: option.id,
                  })) || []
              }
              value={values["conjuntos"]}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderOption={(props, option, { selected }) => (
                <li {...props} key={option.value}>
                  <span>{option.label}</span>
                </li>
              )}
              onClick={() => trackEventMatomo("Aprovações", "click", "select", "seleciona conjuntos")}
              onChange={(_event: AutoCompleteEvent, newValue: { label: string; value: string }[] | null, reason: AutocompleteChangeReason, deatails) => {
                if (reason === "clear" && filter.conjunto && filter.conjunto.length) {
                  removeSons(filter.conjunto, "conjuntos", "unidades", "unidade");
                }
                if (reason === "removeOption") {
                  const removedConjunto = deatails?.option.value as string;
                  removeSons([removedConjunto], "conjuntos", "unidades", "unidade");
                }
                if (reason === "clear") {
                  setFilter((prev) => {
                    delete prev.conjunto;
                    return { ...prev };
                  });
                  setValues((prev) => {
                    if (prev) {
                      prev.conjuntos = [];
                      return prev ? { ...prev } : {};
                    } else return {};
                  });
                } else if (newValue && newValue.length >= 0) {
                  const values = newValue.map((val) => val.value);
                  setValues((prev) => (prev ? { ...prev, conjuntos: newValue } : { conjuntos: newValue }));
                  setFilter((prev) => ({ ...prev, conjunto: values }));
                }
              }}
              renderInput={(params) => <TextField {...params} label={`${t("Aprovations.selectSets")}`} />}
            />
            <Autocomplete
              disabled={loading || !filter.conjunto?.length}
              fullWidth
              noOptionsText={`${t("Aprovations.noMatchingUnits")}`}
              multiple
              options={
                hierarchy?.unidades
                  ?.filter((uni) => values.conjuntos.length && verifySons(values.conjuntos, "conjuntos", uni.id, "unidades"))
                  ?.sort((a, b) => {
                    return a.nome.toLowerCase() > b.nome.toLowerCase() ? 1 : -1;
                  })
                  ?.map((option) => ({
                    label: option.nome,
                    value: option.id,
                  })) || []
              }
              value={values["unidades"]}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderOption={(props, option, { selected }) => (
                <li {...props} key={option.value}>
                  <span>{option.label}</span>
                </li>
              )}
              onClick={() => trackEventMatomo("Aprovações", "click", "select", "seleciona unidades")}
              onChange={(_event: AutoCompleteEvent, newValue: { label: string; value: string }[] | null, reason: AutocompleteChangeReason) => {
                if (reason === "clear" && filter.conjunto && filter.conjunto.length) {
                  setFilter((prev) => ({ ...prev, unidade: undefined }));
                }
                if (reason === "clear") {
                  setFilter((prev) => {
                    delete prev.unidade;
                    return { ...prev };
                  });
                  setValues((prev) => {
                    if (prev) {
                      prev.unidades = [];
                      return prev ? { ...prev } : {};
                    } else return {};
                  });
                } else if (newValue && newValue.length >= 0) {
                  const values = newValue.map((val) => val.value);
                  setValues((prev) => (prev ? { ...prev, unidades: newValue } : { unidades: newValue }));
                  setFilter((prev) => ({ ...prev, unidade: values }));
                }
              }}
              renderInput={(params) => <TextField {...params} label={`${t("Aprovations.selectUnits")}`} />}
            />
            {commingFromHistory && (
              <Stack justifyContent="center" alignItems="center" width="100%">
                <FormControl>
                  <RadioGroup row value={value} onChange={(e) => setValue(e.target.value)}>
                    <FormControlLabel
                      value="aprovados"
                      control={
                        <Radio
                          onClick={() => trackEventMatomo("Aprovações", "click", "radio", "seleciona aprovados")}
                          color="success"
                          onChange={() => setFilter((prev) => ({ ...prev, reprovados: undefined, aprovados: true }))}
                        />
                      }
                      label={<Typography color="success">{t("Aprovations.showOnlyApproved")}</Typography>}
                    />
                    <FormControlLabel
                      value="reprovados"
                      control={
                        <Radio
                          onClick={() => trackEventMatomo("Aprovações", "click", "radio", "seleciona reprovados")}
                          color="error"
                          onChange={() => setFilter((prev) => ({ ...prev, reprovados: true, aprovados: undefined }))}
                        />
                      }
                      label={<Typography color="error">{t("Aprovations.showOnlyRejected")}</Typography>}
                    />
                    <FormControlLabel
                      value="all"
                      control={
                        <Radio
                          color="info"
                          onClick={() => trackEventMatomo("Aprovações", "click", "radio", "seleciona todos")}
                          onChange={() => setFilter((prev) => ({ ...prev, reprovados: true, aprovados: true }))}
                        />
                      }
                      label={<Typography color="info">{t("Aprovations.showAll")}</Typography>}
                    />
                  </RadioGroup>
                </FormControl>
              </Stack>
            )}
            {!commingFromHistory && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filter.mostrarPendentesParaMim !== false}
                    color="success"
                    onChange={(event) => {
                      setFilter((prev) => ({ ...prev, mostrarPendentesParaMim: event.target.checked }));
                      trackEventMatomo("Aprovações", "click", "checkbox", "altera pendentes para mim");
                    }}
                  />
                }
                label={`Mostrar formulários pendentes para ${userData.userName}`}
              />
            )}
          </Stack>
        </Accordion>
      </Stack>
    </Card>
  );
}
